import { createBrowserRouter, Navigate } from "react-router-dom";
import React, { Suspense } from "react";
import Login from "../pages/Login/Login";
import Loader from "../components/Loader/Loader";
import PageNotFound from "../components/PageNotFound/PageNotFound";
import PrivateRoute from "../utils/PrivateRoute";
import ErrorComponent from "../components/ErrorComponent/ErrorComponent";
import { customerType, hasNoProducts, isIndividualUser } from "../utils/utils";
import LinkExpiry from "../components/LinkExpired/LinkExpiry";
import {
  CUSTOMER_TYPES,
  USER_ROLES,
} from "../utils/constants/common";
import AuthCallback from "../pages/AuthCallback/AuthCallback";

const SigninLayout = React.lazy(() => import("../layout/SigninLayout"));
const LandingLayout = React.lazy(() => import("../layout/DashboardLayout"));
const Customer = React.lazy(() => import("../pages/Customer/Customer"));
const Products = React.lazy(() => import("../pages/Products/Products"));
const Users = React.lazy(() => import("../pages/User/User"));
const BlockedIPs = React.lazy(() => import("../pages/BlockedIPs/BlockedIPs"));
const MyAccount = React.lazy(() => import("../pages/Dashboard/MyAccount"));
const ContactUs = React.lazy(() => import("../pages/ContactUs/ContactUs"));
const CustomerDetails = React.lazy(
  () => import("../pages/CustomerDetails/CustomerDetails"),
);
const ProductDetail = React.lazy(
  () => import("../pages/ProductDetail/ProductDetail"),
);
const UserDetail = React.lazy(() => import("../pages/UserDetail/UserDetail"));

const CustomerUserFolderTree = React.lazy(
  () => import("../pages/CustomerUserFolderTree/CustomerUserFolderTree"),
);
const Notification = React.lazy(
  () => import("../pages/Notifications/Notification"),
);

const TestData = React.lazy(() => import("../pages/TestData/TestData"));

const IndexPage = () => {
  const indexpage =
    hasNoProducts() ||
    (isIndividualUser() &&
      (customerType() === CUSTOMER_TYPES.PROD ||
        customerType() === CUSTOMER_TYPES.NO_PROD))
      ? "products"
      : isIndividualUser() && customerType() === CUSTOMER_TYPES.PLATFORM
        ? "platforms"
        : "customers";
  return <Navigate to={indexpage} />;
};
export const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <Suspense fallback={<Loader />}>
        <SigninLayout />
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: <Login />,
      },
    ],
    errorElement: <ErrorComponent />,
  },
  {
    path: "auth/callback",
    element: <AuthCallback />,
  },
  {
    path: "activate/user",
    element: (
      <Suspense fallback={<Loader />}>
        <SigninLayout />
      </Suspense>
    ),
  },
  {
    path: "/home",
    element: (
      <Suspense fallback={<Loader />}>
        <PrivateRoute>
          <LandingLayout />
        </PrivateRoute>
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<Loader />}>
            <PrivateRoute>
              <IndexPage />
            </PrivateRoute>
          </Suspense>
        ),
        errorElement: <ErrorComponent />,
      },

      {
        path: "products",
        element: (
          <Suspense fallback={<Loader />}>
            <PrivateRoute>
              <Products />
            </PrivateRoute>
          </Suspense>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: "products/detail/:id",
        element: (
          <Suspense fallback={<Loader />}>
            <PrivateRoute>
              <ProductDetail />
            </PrivateRoute>
          </Suspense>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: "platforms",
        element: (
          <Suspense fallback={<Loader />}>
            <PrivateRoute>
              <Products />
            </PrivateRoute>
          </Suspense>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: "platforms/detail/:id",
        element: (
          <Suspense fallback={<Loader />}>
            <PrivateRoute>
              <ProductDetail />
            </PrivateRoute>
          </Suspense>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: "documentation",
        element: (
          <Suspense fallback={<Loader />}>
            <PrivateRoute>
              <CustomerUserFolderTree />
            </PrivateRoute>
          </Suspense>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: "designs",
        element: (
          <Suspense fallback={<Loader />}>
            <PrivateRoute>
              <CustomerUserFolderTree />
            </PrivateRoute>
          </Suspense>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: "test_data",
        element: (
          <Suspense fallback={<Loader />}>
            <PrivateRoute>
              <TestData />
            </PrivateRoute>
          </Suspense>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: "users",
        element: (
          <Suspense fallback={<Loader />}>
            <PrivateRoute roles={[USER_ROLES.SUPER_ADMIN]}>
              <Users />
            </PrivateRoute>
          </Suspense>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: "users/detail/:id",
        element: (
          <Suspense fallback={<Loader />}>
            <PrivateRoute roles={[USER_ROLES.SUPER_ADMIN]}>
              <UserDetail />
            </PrivateRoute>
          </Suspense>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: "customers",
        element: (
          <Suspense fallback={<Loader />}>
            <PrivateRoute
              roles={[
                USER_ROLES.SUPER_ADMIN,
                USER_ROLES.ADMIN,
                USER_ROLES.VIEWER,
              ]}
            >
              <Customer />
            </PrivateRoute>
          </Suspense>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: "customers/detail/:id",
        element: (
          <Suspense fallback={<Loader />}>
            <PrivateRoute
              roles={[
                USER_ROLES.SUPER_ADMIN,
                USER_ROLES.ADMIN,
                USER_ROLES.VIEWER,
              ]}
            >
              <CustomerDetails />
            </PrivateRoute>
          </Suspense>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: "blocked_ips",
        element: (
          <Suspense fallback={<Loader />}>
            <PrivateRoute roles={[USER_ROLES.SUPER_ADMIN]}>
              <BlockedIPs />
            </PrivateRoute>
          </Suspense>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: "notifications",
        element: (
          <Suspense fallback={<Loader />}>
            <PrivateRoute>
              <Notification />
            </PrivateRoute>
          </Suspense>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: "contact_us",
        element: (
          <Suspense fallback={<Loader />}>
            <PrivateRoute roles={[USER_ROLES.INDIVIDUAL]}>
              <ContactUs />
            </PrivateRoute>
          </Suspense>
        ),
        errorElement: <ErrorComponent />,
      },
      {
        path: "profile",
        element: (
          <Suspense fallback={<Loader />}>
            <PrivateRoute>
              <MyAccount />
            </PrivateRoute>
          </Suspense>
        ),
      },
    ],
  },
  {
    path: "link-expired",
    element: <LinkExpiry />,
  },
  {
    path: "access-denied",
    element: <PageNotFound noAccess={true} />,
  },
  {
    path: "*",
    element: <PageNotFound />,
  },
]);
